import React, { useEffect, useState } from "react";
import { Loader2, ChevronUp, ChevronDown, Pencil, Save, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { AdminShell } from "./AdminShell";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

export default function AdminQuestions() {
  const [questions, setQuestions] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/questions").then((res) => setQuestions(res.data.questions));
  useEffect(() => { load(); }, []);

  if (!questions) return <AdminShell title="Questions"><div className="grid place-items-center py-20 text-navy"><Loader2 className="animate-spin" /></div></AdminShell>;

  const move = async (idx, dir) => {
    const arr = [...questions];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setQuestions(arr);
    await api.post("/admin/questions/reorder", { codes: arr.map((q) => q.code) });
    toast.success("Order updated.");
  };

  const toggleActive = async (q, active) => {
    setQuestions((qs) => qs.map((x) => (x.code === q.code ? { ...x, active } : x)));
    await api.patch(`/admin/questions/${q.code}`, { active });
    toast.success(active ? "Question enabled." : "Question disabled.");
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/questions/${editing.code}`, {
        text: editing.text,
        overline: editing.overline,
        options: editing.options.map((o) => ({ key: o.key, label: o.label, description: o.description })),
      });
      toast.success("Question saved.");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed.");
    } finally { setSaving(false); }
  };

  return (
    <AdminShell title="Question Editor">
      <div className="max-w-3xl">
        <p className="text-sm text-slate-500 mb-5">
          Edit question wording, answer descriptions, order and visibility. Scores and categories are locked to preserve scoring accuracy.
        </p>
        <div className="space-y-3">
          {questions.map((q, idx) => (
            <div key={q.code} data-testid={`q-row-${q.code}`}
              className="rounded-2xl bg-white border border-slate-200 p-4 card-shadow flex items-start gap-3">
              <div className="flex flex-col items-center pt-1">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-slate-400 hover:text-navy disabled:opacity-30"><ChevronUp size={18} /></button>
                <span className="text-xs font-bold text-slate-400">{q.order}</span>
                <button onClick={() => move(idx, 1)} disabled={idx === questions.length - 1} className="text-slate-400 hover:text-navy disabled:opacity-30"><ChevronDown size={18} /></button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] tracking-wide uppercase font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded">{q.category}</span>
                  {!q.active && <span className="text-[10px] font-bold text-branddanger">DISABLED</span>}
                </div>
                <div className="mt-1.5 text-sm font-semibold text-navy">{q.text}</div>
              </div>
              <div className="flex items-center gap-3 pt-1">
                <Switch checked={q.active} onCheckedChange={(v) => toggleActive(q, v)} data-testid={`q-active-${q.code}`} />
                <button data-testid={`q-edit-${q.code}`} onClick={() => setEditing(JSON.parse(JSON.stringify(q)))}
                  className="rounded-lg border border-slate-200 p-2 text-navy hover:bg-slate-50 transition-colors duration-200"><Pencil size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Edit Question</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-semibold text-slate-600">Overline (category tag)</Label>
                <Input data-testid="edit-overline" value={editing.overline || ""} onChange={(e) => setEditing({ ...editing, overline: e.target.value })} className="mt-1.5 rounded-lg" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Question Text</Label>
                <Textarea data-testid="edit-text" value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} className="mt-1.5 rounded-lg" rows={3} />
              </div>
              <div className="space-y-3">
                <Label className="text-xs font-semibold text-slate-600">Answer Options (score locked)</Label>
                {editing.options.map((o, i) => (
                  <div key={o.key} className="rounded-xl border border-slate-100 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-wide font-bold text-slate-400">{o.key}</span>
                      <span className="text-xs text-slate-400">{o.points === null ? "N/A" : `${o.points} pts`}</span>
                    </div>
                    <Input value={o.label} placeholder="Label" onChange={(e) => {
                      const opts = [...editing.options]; opts[i] = { ...o, label: e.target.value }; setEditing({ ...editing, options: opts });
                    }} className="rounded-lg mb-2 text-sm" />
                    <Textarea value={o.description} placeholder="Description" onChange={(e) => {
                      const opts = [...editing.options]; opts[i] = { ...o, description: e.target.value }; setEditing({ ...editing, options: opts });
                    }} className="rounded-lg text-sm" rows={2} />
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-navy flex items-center gap-1.5"><X size={15} /> Cancel</button>
            <button data-testid="edit-save" onClick={saveEdit} disabled={saving} className="rounded-full bg-navy text-white px-5 py-2 text-sm font-semibold flex items-center gap-1.5 disabled:opacity-60">
              {saving ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}
